import { ReactNode, useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MapPin, Phone, Mail, Facebook, Linkedin, Instagram, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { getProductNames } from "@/data/productList";
import "../styles/logo.css";

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const location = useLocation();
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const aboutDropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }

    const resizeObserver = new ResizeObserver(() => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    });

    if (headerRef.current) resizeObserver.observe(headerRef.current);

    return () => resizeObserver.disconnect();
  }, []);
  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setProductsDropdownOpen(false);
    setAboutDropdownOpen(false);
    setMobileProductsOpen(false);
    setMobileAboutOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const isActive = (path: string) => location.pathname === path;
  const productNames = getProductNames().slice(0, 6);

  const handleMouseEnterProducts = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setProductsDropdownOpen(true);
  };

  const handleMouseLeaveProducts = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setProductsDropdownOpen(false);
    }, 150); // 150ms delay before closing
  };

  const handleMouseEnterAbout = () => {
    if (aboutDropdownTimeoutRef.current) {
      clearTimeout(aboutDropdownTimeoutRef.current);
      aboutDropdownTimeoutRef.current = null;
    }
    setAboutDropdownOpen(true);
  };

  const handleMouseLeaveAbout = () => {
    aboutDropdownTimeoutRef.current = setTimeout(() => {
      setAboutDropdownOpen(false);
    }, 150); // 150ms delay before closing
  };

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "About Us", path: "/about" },
    { label: "Products", path: "/products" },
    { label: "Applications", path: "/applications" },
    { label: "Contact Us", path: "/contact" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header
        ref={headerRef}
        className={`bg-white border-b border-border sticky top-0 ${isScrolled ? 'shadow-lg' : ''} transition-shadow duration-300`}
        style={{ zIndex: 100 }}>
        {/* Top Info Bar */}
        <div className="bg-primary text-primary-foreground py-2 text-xs sm:text-sm">
          <div className="container mx-auto px-4">
            {/* Desktop Layout */}
            <div className="hidden md:flex justify-between items-center">
              <div className="flex gap-6">
                <a
                  href="tel:+919879247225"
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <Phone size={16} />
                  <span>+91 98792 47225</span>
                </a>
                <a
                  href="mailto:contact@rlmineralsmining.com"
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <Mail size={16} />
                  <span>contact@rlmineralsmining.com</span>
                </a>
              </div>
              <div className="flex gap-3">
                <a
                  href="https://facebook.com/RLMineralsMining"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity p-1 hover:bg-white/10 rounded"
                  aria-label="Follow us on Facebook"
                >
                  <Facebook size={18} />
                </a>
                <a
                  href="https://www.linkedin.com/company/rlmineralsmining"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity p-1 hover:bg-white/10 rounded"
                  aria-label="Connect with us on LinkedIn"
                >
                  <Linkedin size={18} />
                </a>
                <a
                  href="https://www.instagram.com/r.l_minerals_mining/?igsh=MWx5dWpqN2RuejB0dw%3D%3D&utm_source=qr#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity p-1 hover:bg-white/10 rounded"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram size={18} />
                </a>
              </div>
            </div>

            {/* Mobile Layout - Wrapped */}
            <div className="md:hidden space-y-2">
              {/* Contact Info Row */}
              <div className="flex flex-wrap gap-3 justify-center">
                <a
                  href="tel:+919879247225"
                  className="flex items-center gap-1 hover:opacity-80 transition-opacity"
                >
                  <Phone size={14} />
                  <span>+91 98792 47225</span>
                </a>
                <a
                  href="mailto:contact@rlmineralsmining.com"
                  className="flex items-center gap-1 hover:opacity-80 transition-opacity"
                >
                  <Mail size={14} />
                  <span>contact@rlmineralsmining.com</span>
                </a>
              </div>

              {/* Social Media Row */}
              <div className="flex gap-3 justify-center">
                <a
                  href="https://facebook.com/RLMineralsMining"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity p-1 hover:bg-white/10 rounded"
                  aria-label="Follow us on Facebook"
                >
                  <Facebook size={16} />
                </a>
                <a
                  href="https://www.linkedin.com/company/rlmineralsmining"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity p-1 hover:bg-white/10 rounded"
                  aria-label="Connect with us on LinkedIn"
                >
                  <Linkedin size={16} />
                </a>
                <a
                  href="https://www.instagram.com/r.l_minerals_mining/?igsh=MWx5dWpqN2RuejB0dw%3D%3D&utm_source=qr#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity p-1 hover:bg-white/10 rounded"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="container mx-auto px-2 py-2">
          <div className="flex justify-between items-center min-h-[60px]">
            {/* Logo */}
            <Link to="/" className="hover:opacity-90 transition-opacity flex-shrink-0">
              <Logo variant="header" showText={true} />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6 xl:gap-8 flex-shrink-0">
              {navLinks.map((link) => (
                link.label === "About Us" ? (
                  <div
                    key={link.path}
                    className="relative group"
                    onMouseEnter={handleMouseEnterAbout}
                    onMouseLeave={handleMouseLeaveAbout}
                  >
                    <Link
                      to={link.path}
                      className={`font-medium transition-colors whitespace-nowrap text-sm xl:text-base relative flex items-center gap-1 py-2 ${isActive(link.path) || location.pathname.startsWith('/infrastructure')
                        ? "text-accent after:absolute after:bottom-[-2px] after:left-0 after:right-0 after:h-0.5 after:bg-accent after:rounded"
                        : "text-foreground hover:text-accent"
                        }`}
                    >
                      {link.label}
                      <ChevronDown size={16} className={`transition-transform ${aboutDropdownOpen ? 'rotate-180' : ''}`} />
                    </Link>

                    {/* About Us Dropdown */}
                    {aboutDropdownOpen && (
                      <div className="absolute top-full left-0 pt-2 w-56 z-50">
                        <div className="absolute top-0 left-0 right-0 h-2 bg-transparent"></div>
                        <div className="dropdown-shadow bg-white border border-border rounded-lg py-2 mt-1">
                          <div className="px-4 py-2 text-xs font-semibold text-primary border-b border-border">
                            About Us
                          </div>
                          <Link
                            to="/about"
                            className="dropdown-item block px-4 py-2 text-sm text-foreground hover:bg-accent/10 hover:text-accent transition-colors"
                            onClick={() => {
                              setAboutDropdownOpen(false);
                              if (aboutDropdownTimeoutRef.current) {
                                clearTimeout(aboutDropdownTimeoutRef.current);
                                aboutDropdownTimeoutRef.current = null;
                              }
                            }}
                          >
                            Company Overview
                          </Link>
                          <Link
                            to="/infrastructure"
                            className="dropdown-item block px-4 py-2 text-sm text-foreground hover:bg-accent/10 hover:text-accent transition-colors"
                            onClick={() => {
                              setAboutDropdownOpen(false);
                              if (aboutDropdownTimeoutRef.current) {
                                clearTimeout(aboutDropdownTimeoutRef.current);
                                aboutDropdownTimeoutRef.current = null;
                              }
                            }}
                          >
                            Infrastructure
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ) : link.label === "Products" ? (
                  <div
                    key={link.path}
                    className="relative group"
                    onMouseEnter={handleMouseEnterProducts}
                    onMouseLeave={handleMouseLeaveProducts}
                  >
                    <Link
                      to={link.path}
                      className={`font-medium transition-colors whitespace-nowrap text-sm xl:text-base relative flex items-center gap-1 py-2 ${isActive(link.path) || location.pathname.startsWith('/products/')
                        ? "text-accent after:absolute after:bottom-[-2px] after:left-0 after:right-0 after:h-0.5 after:bg-accent after:rounded"
                        : "text-foreground hover:text-accent"
                        }`}
                    >
                      {link.label}
                      <ChevronDown size={16} className={`transition-transform ${productsDropdownOpen ? 'rotate-180' : ''}`} />
                    </Link>

                    {/* Products Dropdown */}
                    {productsDropdownOpen && (
                      <div className="absolute top-full left-0 pt-2 w-64 z-50">
                        {/* Invisible bridge to prevent gap issues */}
                        <div className="absolute top-0 left-0 right-0 h-2 bg-transparent"></div>
                        <div className="products-dropdown dropdown-shadow bg-white border border-border rounded-lg py-2 mt-1">
                          <div className="px-4 py-2 text-xs font-semibold text-primary border-b border-border">
                            Our Products
                          </div>
                          {productNames.map((product) => (
                            <Link
                              key={product.id}
                              to={product.path}
                              className="dropdown-item block px-4 py-2 text-sm text-foreground hover:bg-accent/10 hover:text-accent transition-colors"
                              onClick={() => {
                                setProductsDropdownOpen(false);
                                if (dropdownTimeoutRef.current) {
                                  clearTimeout(dropdownTimeoutRef.current);
                                  dropdownTimeoutRef.current = null;
                                }
                              }}
                            >
                              {product.name}
                            </Link>
                          ))}
                          <div className="border-t border-border mt-2 pt-2">
                            <Link
                              to="/products"
                              className="dropdown-item block px-4 py-2 text-sm font-medium text-accent hover:bg-accent/10 transition-colors"
                              onClick={() => {
                                setProductsDropdownOpen(false);
                                if (dropdownTimeoutRef.current) {
                                  clearTimeout(dropdownTimeoutRef.current);
                                  dropdownTimeoutRef.current = null;
                                }
                              }}
                            >
                              View All Products →
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`font-medium transition-colors whitespace-nowrap text-sm xl:text-base relative ${isActive(link.path)
                      ? "text-accent after:absolute after:bottom-[-2px] after:left-0 after:right-0 after:h-0.5 after:bg-accent after:rounded"
                      : "text-foreground hover:text-accent"
                      }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </nav>

            {/* CTA Button */}
            <Link
              to="/contact"
              className="hidden lg:block bg-accent text-accent-foreground px-4 xl:px-6 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity text-sm xl:text-base whitespace-nowrap flex-shrink-0"
            >
              Get Quote
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => {setMobileMenuOpen(!mobileMenuOpen); setMobileAboutOpen(false); setMobileProductsOpen(false);}}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Backdrop - Fixed Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm mobile-menu-backdrop"
          style={{ zIndex: 80, top: '0' }}
          onClick={() => {setMobileMenuOpen(false); setMobileAboutOpen(false); setMobileProductsOpen(false);}}
          aria-hidden="true"
        />
      )}

      {/* Mobile Navigation - Fixed Overlay */}
      {mobileMenuOpen && (
        <nav className=" lg:hidden fixed left-0 right-0 bg-white shadow-2xl overflow-y-auto z-[9999] transition-transform duration-300 translate-y-0"
          style={{
            top: `${headerHeight}px`,
            maxHeight: `calc(100vh - ${headerHeight}px)`
          }}>
          <div className="flex flex-col gap-2 p-4">
            {navLinks.map((link) => (
              link.label === "About Us" ? (
                <div key={link.path} className="flex flex-col">
                  <button
                    onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                    className={`font-medium transition-all duration-200 py-3 px-4 rounded-lg flex items-center justify-between ${isActive(link.path) || location.pathname.startsWith('/infrastructure')
                      ? "text-accent bg-accent/10 border-l-4 border-accent"
                      : "text-foreground hover:text-accent hover:bg-accent/5"
                      }`}
                  >
                    <span>{link.label}</span>
                    <ChevronDown
                      size={18}
                      className={`transition-transform duration-200 ${mobileAboutOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {mobileAboutOpen && (
                    <div className="ml-4 mt-2 space-y-1 animate-slideDown">
                      <Link
                        to="/about"
                        className="block py-2 px-4 text-sm text-foreground/70 hover:text-accent hover:bg-accent/5 rounded transition-colors"
                        onClick={() => {setMobileMenuOpen(false); setMobileAboutOpen(false); setMobileProductsOpen(false);}}
                      >
                        Company Overview
                      </Link>
                      <Link
                        to="/infrastructure"
                        className="block py-2 px-4 text-sm text-foreground/70 hover:text-accent hover:bg-accent/5 rounded transition-colors"
                        onClick={() => {setMobileMenuOpen(false); setMobileAboutOpen(false); setMobileProductsOpen(false);}}
                      >
                        Infrastructure
                      </Link>
                    </div>
                  )}
                </div>
              ) : link.label === "Products" ? (
                <div key={link.path} className="flex flex-col">
                  <button
                    onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                    className={`font-medium transition-all duration-200 py-3 px-4 rounded-lg flex items-center justify-between ${isActive(link.path) || location.pathname.startsWith('/products/')
                      ? "text-accent bg-accent/10 border-l-4 border-accent"
                      : "text-foreground hover:text-accent hover:bg-accent/5"
                      }`}
                  >
                    <span>{link.label}</span>
                    <ChevronDown
                      size={18}
                      className={`transition-transform duration-200 ${mobileProductsOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {mobileProductsOpen && (
                    <div className="ml-4 mt-2 space-y-1 max-h-[40vh] overflow-y-auto mobile-products-submenu animate-slideDown">
                      {productNames.map((product) => (
                        <Link
                          key={product.id}
                          to={product.path}
                          className="block py-2 px-4 text-sm text-foreground/70 hover:text-accent hover:bg-accent/5 rounded transition-colors"
                          onClick={() => {setMobileMenuOpen(false); setMobileAboutOpen(false); setMobileProductsOpen(false);}}
                        >
                          {product.name}
                        </Link>
                      ))}
                      <Link
                        to="/products"
                        className="block py-2 px-4 text-sm font-medium text-accent hover:bg-accent/5 rounded transition-colors border-t border-border mt-2 pt-3"
                        onClick={() => {setMobileMenuOpen(false); setMobileAboutOpen(false); setMobileProductsOpen(false);}}
                      >
                        View All Products →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-all duration-200 py-3 px-4 rounded-lg ${isActive(link.path)
                    ? "text-accent bg-accent/10 border-l-4 border-accent"
                    : "text-foreground hover:text-accent hover:bg-accent/5"
                    }`}
                  onClick={() => {setMobileMenuOpen(false); setMobileAboutOpen(false); setMobileProductsOpen(false);}}
                >
                  {link.label}
                </Link>
              )
            ))}
            <Link
              to="/contact"
              className="bg-accent text-accent-foreground px-4 py-3 rounded-lg font-medium text-center hover:opacity-90 transition-opacity mt-2"
              onClick={() => {setMobileMenuOpen(false); setMobileAboutOpen(false); setMobileProductsOpen(false);}}
            >
              Get Quote
            </Link>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-12">
          <div className="grid 
          grid-cols-1 
          md:grid-cols-4
          md:grid-cols-[1fr_0.7fr_1.5fr_1fr] 
           gap-4 mb-8">
            {/* About */}
            <div>
              <div className="mb-4">
                <Logo variant="footer" showText={true} />
              </div>
              <p className="text-sm opacity-90">
                Leading supplier of industrial minerals and products with over
                15 years of excellence in the industry.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-bold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="opacity-90 hover:opacity-100 transition-opacity"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="font-bold text-lg mb-4">Contact Info</h3>
              <ul className="space-y-2 text-sm opacity-90">
                <li className="flex items-start gap-2">
                  <Phone size={16} className="mt-0.5" />
                  <span className="flex-wrap gap-2">
                  <a href="tel:+919879247225" className="hover:text-white transition-colors whitespace-nowrap">+91 98792 47225</a>
                  <a href="tel:+916375195493" className="hover:text-white transition-colors whitespace-nowrap"> +91 63751 95493</a>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Mail size={16} className="mt-0.5" />
                  <a href="mailto:contact@rlmineralsmining.com">contact@rlmineralsmining.com</a>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin size={16} className="min-w-fit" />
                  {/* /* mt-0 w-8 h-8 md:w-[25px] md:h-[25px] lg:h-[29px] lg:h-[25px]*/}
                  <span className="flex-wrap">
                    S. NO. 222 - 226, opp. Vizag Steel, Azad Nagar, Sarkhej, Ahmedabad, Sarkhej-Okaf, Gujarat 382210</span>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="font-bold text-lg mb-4">Our Services</h3>
              <ul className="space-y-2 text-sm opacity-90">
                <li>Quality Products</li>
                <li>Timely Delivery</li>
                <li>Expert Support</li>
                <li>Bulk Orders</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-2 text-center text-sm text-white">
            <p className="flex flex-col md:flex-row justify-center items-center gap-1 md:gap-2">
              <span>
                &copy; 2025{" "}
                <span className="font-semibold text-white">
                  R.L Minerals Mining.
                </span>
                {" "}All rights reserved.
              </span>
              <span className="hidden md:inline text-white/50">|</span>
              <a
                  href="https://synergyhexatech.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white font-medium transition-colors"
                >
              <span>
                Website developed by{" "}SynergyHexaTech.
              </span>
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
